import { ReactEditor, useSlateStatic } from 'slate-react'
import type { RefObject } from 'react'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { VirtualReference } from '../../utils/popper'
import useEventHandler from '../../../../../utils/hooks/useEventHandler'

type TooltipManagementReturn = [RefObject<HTMLInputElement | null>, HTMLElement | null]

export default function useTooltipManagement(): TooltipManagementReturn {
  const inputRef = useRef<HTMLInputElement>(null)
  const editor = useSlateStatic()
  const [virtualReferenceElement, setVirtualReferenceElement] = useState(null)
  const lastSelection = editor.useLastSelection(editor)

  const updatePosition = useCallback(() => {
    if (!editor.selection && !lastSelection?.current) return

    // When an empty text node is selected, .toDOMRange will throw.
    // (because it won't find a corresponding DOM node)
    let domRange
    try {
      const currentSelection = editor.selection || lastSelection.current
      if (currentSelection) {
        domRange = ReactEditor.toDOMRange(editor, currentSelection)
      }
    } catch {
      const domSelection = window.getSelection()
      if (domSelection?.rangeCount) domRange = domSelection.getRangeAt(0)
    }
    const rect = domRange?.getBoundingClientRect()
    const offsetParentRect = domRange?.commonAncestorContainer?.parentNode?.offsetParent?.getBoundingClientRect()

    if (rect) setVirtualReferenceElement(new VirtualReference({ clientRect: rect }))

    // hide tooltip if outside the scroll parent's bounds
    if (offsetParentRect && (rect.top < offsetParentRect.top || rect.bottom > offsetParentRect.bottom)) {
      setVirtualReferenceElement(null)
    }
  }, [editor, lastSelection])

  useEventHandler('scroll', updatePosition, window, { capture: true, passive: true })

  useLayoutEffect(() => {
    if (editor.selection && ReactEditor.isFocused(editor)) updatePosition()
  }, [editor, editor.selection, updatePosition])

  return [inputRef, virtualReferenceElement]
}
